<template>
  <div
    class="audio-item-box"
    :class="{
      'audio-item-box-mobile': createRenderType == 'mobile',
    }"
  >
    <template v-if="renderData.pitem?.songs?.length">
      <template
        v-for="(item, index) in getRenderMediasData(renderData.pitem?.songs)"
        :key="item?.song_id"
      >
        <AudioItem
          v-if="!item.recall"
          :item="item"
          :pitem="props.pitem"
          @restore="doRestore"
          @regenerate="doRegenerate"
          @extend="doExtend"
          @togglePlay="doTogglePlay"
          @download="doDownload"
          @addShoping="doAddShoping"
          @soldOut="doSoldOut"
          @editSongTitle="doEditSongTitle"
          @share="doShare"
          @delete="doDelete"
          @like="doLike"
          @goDetail="doGoDetail"
          @downloadMp3="doDownloadMp3"
          @downloadCopyright="doDownloadCopyright"
          @downloadWAVAndStem="doDownloadWAVAndStem"
          :listRenderType="props.listRenderType"
        ></AudioItem>
      </template>
    </template>
    <template v-else>
      <template
        v-for="(item, index) in [2, 3].includes(
          props.pitem?.generation_method
        ) && props.pitem?.is_generated_from_uploaded_file
          ? 1
          : 2"
        :key="index"
      >
        <AudioItemSkeleton :pitem="props.pitem"></AudioItemSkeleton>
      </template>
    </template>
    <Transition name="slide-fade">
      <div
        class="generate-step-box"
        @touchmove.stop
        v-if="
          ModuleOpenStateControl.create.generateStepProgress &&
          [1, 2].includes(renderData.pitem?.state) &&
          renderData.thinkingContent != null &&
          renderData.generateStepVisible
        "
      >
        <div class="generate-step-main">
          <GenerateStep
            :pitem="props.pitem"
            :item="props.pitem?.songs?.[0]"
            :index="props.index"
            :isWriting="true"
          ></GenerateStep>
        </div>

        <div class="generate-step-close-box">
          <IconButton
            size="24px"
            icoSize="12px"
            backgroundColor="rgb(255, 255, 255, 0.1)"
            hoverBackgroundColor="rgb(255, 255, 255, 0.2)"
            @onClick="closeThinking"
          >
            <img src="@/assets/img/close-ico.png" />
          </IconButton>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
} from "vue"

import { isDev } from "@/utils/tools.js"
import AudioItem from "./audioItem.vue"
import AudioItemSkeleton from "./audioItemSkeleton.vue"
import IconButton from "@/components/basic/button/IconButton.vue"

import globalConfig, {
  chinaAppName,
  ModuleOpenStateControl,
  getPipeReadableFlowUrl,
} from "@/config/config.js"
import GenerateStep from "@/page/create/generateStep/generateStep.vue"

import { storeToRefs } from "pinia"
import useCreateLayoutStore from "@/store/create/createLayoutStore.js"
import useFeedListStore from "@/store/create/feedListDataStore.js"

const feedListDataStore = useFeedListStore()

const createLayoutStore = useCreateLayoutStore()
const { createRenderType } = storeToRefs(createLayoutStore)

const getRenderMediasData = (datas) => {
  if (Array.isArray(datas) && datas.length) {
    return [...datas.slice(0, 3)]
  }
  return []
}

const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  pitem: {
    type: Object,
    default: null,
  },
  listRenderType: {
    type: String,
    default: "createdresult", // mysong, likemysong, createdresult
  },
})

const renderData = reactive({
  pitem: props.pitem,
  generateStepVisible:
    [1, 2].includes(props.pitem?.state) && props.pitem?.with_thinking_process,
  thinkingContent: props.pitem?.with_thinking_process
    ? props.pitem?.content ?? ""
    : null,
})
const emits = defineEmits([
  "restore",
  "regenerate",
  "from",
  "delete",
  "like",
  "more",
  "togglePlay",
  "download",
  "addShoping",
  "soldOut",
  "editSongTitle",
  "share",
  "goDetail",
  "downloadMp3",
  "downloadCopyright",
  "downloadWAVAndStem",
])

const closeThinking = () => {
  renderData.generateStepVisible = false
  feedListDataStore.closeThinkingProcessCard(props.pitem)
}

const doRestore = (pitem, item) => {
  emits("restore", pitem, item)
}

const doRegenerate = (pitem, item) => {
  emits("regenerate", pitem, item)
}

const doGoDetail = (pitem, item) => {
  emits("goDetail", pitem, item)
}

const doExtend = (pitem, item) => {
  emits("extend", pitem, item)
}

const doFrom = (pitem, item) => {
  emits("from", pitem, item)
}
const doDelete = (pitem, item) => {
  emits("delete", pitem, item)
}

const doLike = (pitem, item) => {
  emits("like", pitem, item)
}

const doMore = (pitem, item) => {
  emits("more", pitem, item)
}

const doTogglePlay = (pitem, item, isPlay) => {
  emits("togglePlay", pitem, item, isPlay)
}

const doDownload = (pitem, item) => {
  emits("download", pitem, item)
}

const doDownloadMp3 = (pitem, item) => {
  emits("downloadMp3", pitem, item)
}

const doDownloadCopyright = (pitem, item) => {
  emits("downloadCopyright", pitem, item)
}

const doDownloadWAVAndStem = (pitem, item) => {
  emits("downloadWAVAndStem", pitem, item)
}

const doAddShoping = (pitem, item) => {
  emits("addShoping", pitem, item)
}

const doSoldOut = (pitem, item) => {
  emits("soldOut", pitem, item)
}

const doEditSongTitle = (pitem, item) => {
  emits("editSongTitle", pitem, item)
}

const doShare = (pitem, item, callback) => {
  emits("share", pitem, item, callback)
}
watch(
  () => {
    return props.pitem
  },
  () => {
    if (renderData.pitem?.with_thinking_process) {
      renderData.thinkingContent = props.pitem?.content ?? ""
    }
    renderData.pitem = props.pitem
    if (renderData.pitem?.state > 2 && renderData.generateStepVisible) {
      renderData.generateStepVisible = false
    }
  }
)
onMounted(() => {
  renderData.pitem = props.pitem
  if (renderData.pitem?.with_thinking_process) {
    renderData.thinkingContent = props.pitem?.content ?? ""
  }
  renderData.generateStepVisible =
    [1, 2].includes(props.pitem?.state) && props.pitem?.with_thinking_process
})
</script>
<style lang="scss">
.audio-item-box {
  position: relative;
  .generate-step-box {
    position: absolute;
    top: 12px;
    left: 110px;
    right: 18px;
    bottom: 12px;
    max-height: (calc(100% - 24px));
  }
  .generate-step-close-box {
    position: absolute;
    top: -12px;
    right: -12px;
    .icon-button {
      backdrop-filter: blur(10px);
    }
  }
  .slide-fade-enter-active {
    transition: all 0.25s ease-out;
  }

  .slide-fade-leave-active {
    transition: all 0.25s cubic-bezier(1, 0.5, 0.8, 1);
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateX(100%);
    opacity: 0;
  }
  .generate-step-main {
    height: 100%;
    width: 100%;
  }
  &.audio-item-box-mobile {
    .generate-step-box {
      right: 16px;
      left: 98px;
    }
  }
}
</style>
